import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import type { CreateProductDTO, UpdateProductDTO } from '../types';

const createSchema = z.object({
  slug: z
    .string()
    .min(1)
    .regex(/^[a-z0-9-]+$/, 'Slug must be lowercase letters, numbers, and hyphens only'),
  name_en: z.string().min(1),
  name_ar: z.string().min(1),
  description_en: z.string().optional().nullable(),
  redirect_url: z.string().url(),
  redirect_secret: z.string().min(8),
  logo_url: z.string().url().optional().nullable().or(z.literal('')),
});

const updateSchema = createSchema.omit({ redirect_secret: true });

type CreateFormValues = z.infer<typeof createSchema>;
type UpdateFormValues = z.infer<typeof updateSchema>;

interface ProductFormProps {
  mode: 'create' | 'edit';
  defaultValues?: Partial<UpdateFormValues>;
  onSubmit: (data: CreateProductDTO | UpdateProductDTO) => Promise<void>;
  isSubmitting: boolean;
  submitError?: string | null;
  submitSuccess?: string | null;
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function ProductForm({
  mode,
  defaultValues,
  onSubmit,
  isSubmitting,
  submitError,
  submitSuccess,
}: ProductFormProps) {
  const { t } = useTranslation();
  const isCreate = mode === 'create';

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateFormValues>({
    resolver: zodResolver(isCreate ? createSchema : updateSchema) as never,
    defaultValues: {
      slug: defaultValues?.slug ?? '',
      name_en: defaultValues?.name_en ?? '',
      name_ar: defaultValues?.name_ar ?? '',
      description_en: defaultValues?.description_en ?? '',
      redirect_url: defaultValues?.redirect_url ?? '',
      redirect_secret: '',
      logo_url: defaultValues?.logo_url ?? '',
    },
  });

  const nameEn = watch('name_en');

  useEffect(() => {
    if (isCreate && nameEn) {
      setValue('slug', slugify(nameEn), { shouldValidate: false });
    }
  }, [nameEn, isCreate, setValue]);

  const handleFormSubmit = handleSubmit(async (values) => {
    const payload = isCreate
      ? {
          slug: values.slug,
          name_en: values.name_en,
          name_ar: values.name_ar,
          description_en: values.description_en || null,
          redirect_url: values.redirect_url,
          redirect_secret: values.redirect_secret,
          logo_url: values.logo_url || null,
        }
      : {
          slug: values.slug,
          name_en: values.name_en,
          name_ar: values.name_ar,
          description_en: values.description_en || null,
          redirect_url: values.redirect_url,
          logo_url: values.logo_url || null,
        };

    await onSubmit(payload);
  });

  return (
    <form onSubmit={handleFormSubmit} className="space-y-5">
      {submitError ? <p className="text-sm text-destructive">{submitError}</p> : null}
      {submitSuccess ? <p className="text-sm text-emerald-600">{submitSuccess}</p> : null}
      <div className="space-y-1.5">
        <Label htmlFor="name_en">{t('products.name')}</Label>
        <Input id="name_en" {...register('name_en')} aria-invalid={Boolean(errors.name_en)} />
        {errors.name_en && <p className="text-xs text-destructive">{errors.name_en.message}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="name_ar">{t('products.nameAr')}</Label>
        <Input
          id="name_ar"
          dir="rtl"
          {...register('name_ar')}
          aria-invalid={Boolean(errors.name_ar)}
        />
        {errors.name_ar && <p className="text-xs text-destructive">{errors.name_ar.message}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="slug">{t('products.slug')}</Label>
        <Input id="slug" {...register('slug')} aria-invalid={Boolean(errors.slug)} />
        {errors.slug && <p className="text-xs text-destructive">{errors.slug.message}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="description_en">{t('products.description')}</Label>
        <Input
          id="description_en"
          {...register('description_en')}
          aria-invalid={Boolean(errors.description_en)}
        />
        {errors.description_en && (
          <p className="text-xs text-destructive">{errors.description_en.message}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="redirect_url">{t('products.redirectUrl')}</Label>
        <Input
          id="redirect_url"
          type="url"
          {...register('redirect_url')}
          aria-invalid={Boolean(errors.redirect_url)}
        />
        {errors.redirect_url && (
          <p className="text-xs text-destructive">{errors.redirect_url.message}</p>
        )}
      </div>

      {isCreate && (
        <div className="space-y-1.5">
          <Label htmlFor="redirect_secret">{t('products.redirectSecret')}</Label>
          <Input
            id="redirect_secret"
            type="password"
            {...register('redirect_secret')}
            aria-invalid={Boolean(errors.redirect_secret)}
          />
          {errors.redirect_secret && (
            <p className="text-xs text-destructive">{errors.redirect_secret.message}</p>
          )}
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="logo_url">{t('products.logoUrl')}</Label>
        <Input
          id="logo_url"
          type="url"
          {...register('logo_url')}
          aria-invalid={Boolean(errors.logo_url)}
        />
        {errors.logo_url && <p className="text-xs text-destructive">{errors.logo_url.message}</p>}
      </div>

      <Button type="submit" isLoading={isSubmitting} className="w-full">
        {isCreate ? t('products.create') : t('products.edit')}
      </Button>
    </form>
  );
}
